/** Auto-generated from EncycGames_Universal_Movement_Psychomotor_Superset_3Sports.xlsx — do not hand-edit rows. */
/* eslint-disable */

export type SportFit = 'Direct' | 'Transfer' | 'Limited' | 'N/A' | string;

export interface MotorSportMapping {
  application: string;
  fit: SportFit;
}

export interface MotorCapability {
  id: string;
  name: string;
  category: string;
  frameworkLayer: string;
  reviewStatus: string;
  reviewNotes: string;
  bySport: Record<string, MotorSportMapping>;
}

export interface FrameworkLayer {
  id: string;
  name: string;
  meaning: string;
  useInPlatform: string;
  count: number | null;
}

export interface DesignDecision {
  decision: string;
  why: string;
  consequence: string;
}

export interface FitLegendItem {
  code: string;
  meaning: string;
}

export interface FrameworkReference {
  title: string;
  summary: string;
  url: string;
}

export interface MotorSportOption {
  id: string;
  label: string;
}

/** Current mapped sports — expand this list as more sports are added to the superset. */
export const MOTOR_SPORT_OPTIONS: MotorSportOption[] = [
  {
    "id": "badminton",
    "label": "Badminton"
  },
  {
    "id": "cricket",
    "label": "Cricket"
  },
  {
    "id": "football",
    "label": "Football"
  }
];

export const MOTOR_FRAMEWORK_PURPOSE = "Purpose: maintain one broad all-sport movement/psychomotor master taxonomy, then map each capability to a sport as Direct, Transfer, Limited or N/A. The master taxonomy intentionally includes classical FMS, broader movement patterns, equipment/environment movement, physical capacities and perceptual-motor control.";

export const MOTOR_FRAMEWORK_LAYERS: FrameworkLayer[] = [
  {
    "id": "traditional-fms",
    "name": "Traditional FMS",
    "meaning": "Classical locomotor, non-locomotor/stability and manipulative/object-control patterns commonly used in school PE and motor-development frameworks.",
    "useInPlatform": "Foundation movement literacy; especially useful for children and beginners.",
    "count": 30
  },
  {
    "id": "broad-movement-foundation",
    "name": "Broad Movement Foundation",
    "meaning": "Broader human movement/task patterns such as crawling, climbing, push/pull, carrying, gripping, weight transfer and hanging.",
    "useInPlatform": "Extends beyond compact school-FMS lists so the master taxonomy can support many sports.",
    "count": 12
  },
  {
    "id": "sport-movement-foundation",
    "name": "Sport Movement Foundation",
    "meaning": "Movement-quality patterns highly relevant to safe/effective sport performance: landing, bracing, lunging, acceleration, deceleration, COD, safe falling.",
    "useInPlatform": "Prerequisite and progression benchmarks before high-intensity sport execution.",
    "count": 10
  },
  {
    "id": "object-control-extension",
    "name": "Object-Control Extension",
    "meaning": "Manipulation skills not always separated in compact FMS lists, such as sidearm throwing, serving, receiving/trapping and head/body striking.",
    "useInPlatform": "Bridges generic object control to sport-specific technical skill.",
    "count": 4
  },
  {
    "id": "perceptual-motor-foundation",
    "name": "Perceptual-Motor Foundation",
    "meaning": "Perception-to-action capabilities: tracking, scanning, reaction, spatial awareness, timing/rhythm and eye-hand/eye-foot coordination.",
    "useInPlatform": "Applies from grassroots to elite; increase complexity, speed and uncertainty by level.",
    "count": 6
  },
  {
    "id": "psychomotor-control-extension",
    "name": "Psychomotor Control Extension",
    "meaning": "Sensorimotor/precision control such as bilateral coordination, proprioception, anticipation, force grading, trajectory perception and fine hand control.",
    "useInPlatform": "Allows progressive assessment of movement control, not just whether the movement can be performed.",
    "count": 6
  },
  {
    "id": "equipment-environment-movement",
    "name": "Equipment & Environment Movement",
    "meaning": "Movement through water, snow/ice, wheels, boards, paddles and adaptive equipment.",
    "useInPlatform": "Needed for a true all-sport superset, including adaptive/para and environment-specific sports.",
    "count": 8
  },
  {
    "id": "physical-capacity",
    "name": "Physical Capacity",
    "meaning": "Fitness capacities that enable movement: coordination, flexibility/mobility, agility, strength, muscular endurance, cardiovascular endurance, speed and power.",
    "useInPlatform": "Keep scores distinct from technical motor-skill scores even though they influence sport performance.",
    "count": 8
  },
  {
    "id": "advanced-optional-extension",
    "name": "Advanced / Optional Extension",
    "meaning": "Acrobatic or specialist patterns that should not be universal prerequisites, e.g. inversion and vaulting.",
    "useInPlatform": "Use only when age-, sport-, environment- and ability-appropriate.",
    "count": 2
  }
];

export const MOTOR_DESIGN_DECISIONS: DesignDecision[] = [
  {
    "decision": "Use a universal superset, not one national FMS list as the master.",
    "why": "Compact FMS lists are designed for teaching core childhood skills; an all-sport platform must also cover equipment/environment movement, adaptive movement, physical capacities and perception-action control.",
    "consequence": "Each sport selects and weights only the capabilities it needs; the master list can remain stable as new sports are added."
  },
  {
    "decision": "Do not call every row a 'motor skill'.",
    "why": "Strength, speed and cardiovascular endurance are physical capacities; scanning and anticipation are perceptual/psychomotor; swimming/cycling are environment/equipment movement modes.",
    "consequence": "Use one master 'movement / motor capability' taxonomy with clear layers, then build separate score types."
  },
  {
    "decision": "Psychomotor is not an elite-only layer.",
    "why": "Basic perception-action, balance, coordination, timing and reaction begin in childhood; elite performance increases precision, speed, uncertainty and decision pressure.",
    "consequence": "Create age/level progressions for the same capability rather than hiding psychomotor assessment until elite levels."
  },
  {
    "decision": "Keep Direct vs Transfer explicit.",
    "why": "A movement may develop a useful pattern without literally being a sport action.",
    "consequence": "Prevents biomechanical analogies from being represented as sport techniques."
  },
  {
    "decision": "Preserve adaptive/para movement in the master taxonomy.",
    "why": "A universal sports framework should not assume running/standing locomotion is the only valid movement mode.",
    "consequence": "Equipment/adaptive movement can map directly where relevant, e.g. wheelchair para-badminton."
  }
];

export const MOTOR_FIT_LEGEND: FitLegendItem[] = [
  {
    "code": "Direct",
    "meaning": "Recognizable movement/capability used in match/play or as a fundamental sport capacity."
  },
  {
    "code": "Transfer",
    "meaning": "Useful movement/coordination/conditioning transfer, but not literally a sport action."
  },
  {
    "code": "Limited",
    "meaning": "Rare, position-/format-/adaptive-specific, or only partly relevant."
  },
  {
    "code": "N/A",
    "meaning": "No meaningful direct application in that sport format."
  }
];

export const MOTOR_FRAMEWORK_REFERENCES: FrameworkReference[] = [
  {
    "title": "Australian Sports Commission — Physical Literacy, Physical domain",
    "summary": "Movement skills; moving with equipment; object manipulation; coordination; stability/balance; flexibility; agility; strength; muscular endurance; cardiovascular endurance; reaction time; speed.",
    "url": "https://www.ausport.gov.au/physical_literacy/physical"
  },
  {
    "title": "NSW Department of Education — Fundamental Movement Skills",
    "summary": "K–6 compact FMS teaching set; 12 skills are progressively introduced and refined as building blocks for more complex/specialised movement.",
    "url": "https://education.nsw.gov.au/teaching-and-learning/curriculum/pdhpe/planning-programming-and-assessing-pdhpe-k-12/planning-programming-and-assessing-pdhpe-k-6"
  },
  {
    "title": "SHAPE America — 2024 National PE Standards",
    "summary": "Broad grade-span motor learning includes locomotor/non-locomotor skills, balance, weight transfer, bouncing, rolling, throwing, catching, kicking, dribbling, striking, sending/receiving, swimming/water safety and more.",
    "url": "https://www.shapeamerica.org/standards/pe/new-pe-standards/"
  }
];

export const MOTOR_CAPABILITIES: MotorCapability[] = [
  {
    "id": "walking",
    "name": "Walking",
    "category": "Locomotor",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Keep as a base locomotor skill; avoid implying walking is a primary movement during active badminton rallies.",
    "bySport": {
      "badminton": {
        "application": "Between points; low-intensity repositioning and recovery in training",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Fielders repositioning between deliveries; low-intensity movement in the field",
        "fit": "Direct"
      },
      "football": {
        "application": "Low-intensity repositioning during stoppages and recovery phases",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "running",
    "name": "Running",
    "category": "Locomotor",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Direct across all three sports.",
    "bySport": {
      "badminton": {
        "application": "Court coverage when distance opens up; approach/recovery combined with chasse, shuffle and lunge",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Sprinting between wickets; chasing, backing up and boundary coverage",
        "fit": "Direct"
      },
      "football": {
        "application": "Off-the-ball runs, pressing, recovery runs and tracking opponents",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "jumping",
    "name": "Jumping",
    "category": "Locomotor",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Direct across all three sports.",
    "bySport": {
      "badminton": {
        "application": "Jump smash, rear-court interception and explosive overhead reach",
        "fit": "Direct"
      },
      "cricket": {
        "application": "High/boundary catches and jumping to intercept the ball",
        "fit": "Direct"
      },
      "football": {
        "application": "Heading, aerial challenges and goalkeeper saves",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "hopping",
    "name": "Hopping",
    "category": "Locomotor",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Useful but often appears as a transition rather than a named sport technique.",
    "bySport": {
      "badminton": {
        "application": "Single-leg adjustment/recovery after reaching or landing; rhythm drills",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Single-leg stride adjustment before a catch/throw; balance during fielding drills",
        "fit": "Transfer"
      },
      "football": {
        "application": "Single-leg adjustment before cutting, striking or landing",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "bounding",
    "name": "Bounding",
    "category": "Locomotor",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Mainly a power/locomotor transfer pattern rather than a named game action.",
    "bySport": {
      "badminton": {
        "application": "Explosive multi-step push-off toward the forecourt/rear court; power footwork drills",
        "fit": "Transfer"
      },
      "cricket": {
        "application": "Bowler approach/gather power and explosive fielding chase",
        "fit": "Transfer"
      },
      "football": {
        "application": "Explosive acceleration into a sprint, press or tackle",
        "fit": "Transfer"
      }
    }
  },
  {
    "id": "skipping",
    "name": "Skipping",
    "category": "Locomotor",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – corrected",
    "reviewNotes": "The original sport examples overstated direct match use. Skipping is valuable mainly as a coordination pattern.",
    "bySport": {
      "badminton": {
        "application": "Rhythm, coordination and light footwork drills; step-pattern transfer",
        "fit": "Transfer"
      },
      "cricket": {
        "application": "Rhythm and coordination drills; limited direct match use",
        "fit": "Transfer"
      },
      "football": {
        "application": "Rhythm/coordination training and light recovery footwork; limited direct match use",
        "fit": "Transfer"
      }
    }
  },
  {
    "id": "galloping-side-gallop",
    "name": "Galloping / Side Gallop",
    "category": "Locomotor",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – corrected",
    "reviewNotes": "Do not equate galloping with every lateral shuffle; treat it as a transferable step-close pattern.",
    "bySport": {
      "badminton": {
        "application": "Step-close rhythm transfers to chasse/lateral court recovery",
        "fit": "Transfer"
      },
      "cricket": {
        "application": "Step-close lateral movement in fielding and warm-up drills",
        "fit": "Transfer"
      },
      "football": {
        "application": "Step-close lateral movement transfers to jockeying and defensive recovery",
        "fit": "Transfer"
      }
    }
  },
  {
    "id": "sliding-side-shuffle",
    "name": "Sliding / Side Shuffle",
    "category": "Locomotor",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Clarified as side shuffle/slide to avoid confusion with ground sliding tackles.",
    "bySport": {
      "badminton": {
        "application": "Rapid lateral defensive coverage and recovery",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Lateral fielding to cut off the ball or protect the boundary",
        "fit": "Direct"
      },
      "football": {
        "application": "Jockeying, lateral marking and defensive positioning",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "leaping",
    "name": "Leaping",
    "category": "Locomotor",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Leap is one-foot take-off to opposite-foot/two-foot landing; distinguish from a two-foot jump.",
    "bySport": {
      "badminton": {
        "application": "Long one-foot take-off reach to intercept a distant shuttle; emergency court coverage",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Leaping/diving catches and airborne fielding interceptions",
        "fit": "Direct"
      },
      "football": {
        "application": "Aerial interception, goalkeeper take-off and long-stride reach",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "dodging",
    "name": "Dodging",
    "category": "Locomotor",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – corrected",
    "reviewNotes": "Cricket is not N/A, but the direct match application is weaker than in football.",
    "bySport": {
      "badminton": {
        "application": "Rapid evasive direction change and deceptive movement during court coverage",
        "fit": "Transfer"
      },
      "cricket": {
        "application": "Agility/fielding drills; evasive movement around other players or obstacles",
        "fit": "Transfer"
      },
      "football": {
        "application": "Dribbling past defenders, body feints and evasive runs",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "underhand-throw",
    "name": "Underhand Throw",
    "category": "Object Control / Manipulative",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – corrected",
    "reviewNotes": "Major correction: badminton flick/low serve should not be classified as an underhand throw.",
    "bySport": {
      "badminton": {
        "application": "No direct match skill; useful for object-control warm-ups. A badminton serve is an implement strike, not a throw.",
        "fit": "Transfer"
      },
      "cricket": {
        "application": "Short-distance underarm return/relay to the wicketkeeper, bowler or nearby fielder",
        "fit": "Direct"
      },
      "football": {
        "application": "Goalkeeper underarm distribution/roll-toss and training tosses",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "overhand-throw",
    "name": "Overhand Throw",
    "category": "Object Control / Manipulative",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – corrected",
    "reviewNotes": "Badminton smash is an implement strike, not an overhand throw.",
    "bySport": {
      "badminton": {
        "application": "No direct throw; overhead strokes share some kinetic-chain sequencing only",
        "fit": "Transfer"
      },
      "cricket": {
        "application": "Fielding throws to the stumps/keeper from distance",
        "fit": "Direct"
      },
      "football": {
        "application": "Two-handed throw-in and goalkeeper overarm distribution",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "catching",
    "name": "Catching",
    "category": "Object Control / Manipulative",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Football application is position/rule dependent.",
    "bySport": {
      "badminton": {
        "application": "No direct match use; useful in general object-control training",
        "fit": "Transfer"
      },
      "cricket": {
        "application": "Wicketkeeping, slips, close catching, outfield and high catches",
        "fit": "Direct"
      },
      "football": {
        "application": "Goalkeeper handling; outfield players cannot intentionally catch with the hands",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "kicking",
    "name": "Kicking",
    "category": "Object Control / Manipulative",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Keep as a core object-control FMS because it is foundational to football.",
    "bySport": {
      "badminton": {
        "application": "No direct match use",
        "fit": "N/A"
      },
      "cricket": {
        "application": "No core cricket skill; foot may contact the ball incidentally but kicking is not a standard technical skill",
        "fit": "N/A"
      },
      "football": {
        "application": "Passing, shooting, crossing, clearing and set pieces",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "punting",
    "name": "Punting",
    "category": "Object Control / Manipulative",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Position-specific in football.",
    "bySport": {
      "badminton": {
        "application": "No direct match use",
        "fit": "N/A"
      },
      "cricket": {
        "application": "No direct match use",
        "fit": "N/A"
      },
      "football": {
        "application": "Goalkeeper punt/drop-kick distribution",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "dribbling-hands",
    "name": "Dribbling (Hands)",
    "category": "Object Control / Manipulative",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Retain for general motor literacy, but it has little direct relevance to these three sports.",
    "bySport": {
      "badminton": {
        "application": "No direct match use",
        "fit": "N/A"
      },
      "cricket": {
        "application": "No direct match use",
        "fit": "N/A"
      },
      "football": {
        "application": "No standard outfield match use; ball bouncing by a goalkeeper is not basketball-style dribbling",
        "fit": "N/A"
      }
    }
  },
  {
    "id": "dribbling-feet",
    "name": "Dribbling (Feet)",
    "category": "Object Control / Manipulative",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Direct football skill.",
    "bySport": {
      "badminton": {
        "application": "No direct match use",
        "fit": "N/A"
      },
      "cricket": {
        "application": "No direct match use",
        "fit": "N/A"
      },
      "football": {
        "application": "Running with the ball, close control and carrying into space",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "striking-hand",
    "name": "Striking (Hand)",
    "category": "Object Control / Manipulative",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – corrected",
    "reviewNotes": "The original goalkeeper example was useful; cricket mapping removed.",
    "bySport": {
      "badminton": {
        "application": "No legal direct hand strike of the shuttle in match play",
        "fit": "N/A"
      },
      "cricket": {
        "application": "No direct batting application; glove/hand deflections are better classified as catching/blocking",
        "fit": "N/A"
      },
      "football": {
        "application": "Goalkeeper punching/parrying the ball",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "striking-implement",
    "name": "Striking (Implement)",
    "category": "Object Control / Manipulative",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "One of the strongest shared base-skill mappings for badminton and cricket.",
    "bySport": {
      "badminton": {
        "application": "Racket contact for serves, clears, drops, drives, smashes and net shots",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Batting: drives, pulls, cuts, sweeps and other bat-ball contacts",
        "fit": "Direct"
      },
      "football": {
        "application": "No standard implement is used in match play",
        "fit": "N/A"
      }
    }
  },
  {
    "id": "volleying-striking-before-ground-contact",
    "name": "Volleying / Striking Before Ground Contact",
    "category": "Object Control / Manipulative",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – corrected",
    "reviewNotes": "Major correction: a sharp cricket catch should not be labelled volleying.",
    "bySport": {
      "badminton": {
        "application": "Direct: the shuttle must be struck before it lands; net interceptions are clear examples",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Limited analogy: striking a full toss before bounce; catching a line drive is catching, not volleying",
        "fit": "Limited"
      },
      "football": {
        "application": "First-time shots, volleys and aerial clearances",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "rolling",
    "name": "Rolling",
    "category": "Object Control / Manipulative",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Useful object-control pattern.",
    "bySport": {
      "badminton": {
        "application": "No direct match use",
        "fit": "N/A"
      },
      "cricket": {
        "application": "Underarm ground return/controlled roll in close fielding drills",
        "fit": "Direct"
      },
      "football": {
        "application": "Goalkeeper rolling distribution along the ground",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "static-balance",
    "name": "Static Balance",
    "category": "Stability / Non-locomotor",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Direct foundation across all three.",
    "bySport": {
      "badminton": {
        "application": "Ready stance, serve/receive setup and controlled pauses",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Batting stance, set position before delivery and fielding ready stance",
        "fit": "Direct"
      },
      "football": {
        "application": "Set-piece stance, single-leg plant preparation and goalkeeper ready stance",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "dynamic-balance",
    "name": "Dynamic Balance",
    "category": "Stability / Non-locomotor",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Direct foundation across all three.",
    "bySport": {
      "badminton": {
        "application": "Maintaining control through lunges, changes of direction and stroke recovery",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Running/fielding while tracking the ball; batting movement under control",
        "fit": "Direct"
      },
      "football": {
        "application": "Dribbling, shielding, turning and contact situations",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "bending",
    "name": "Bending",
    "category": "Stability / Non-locomotor",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Direct foundation.",
    "bySport": {
      "badminton": {
        "application": "Lowering to retrieve/meet low net shots and maintain a low defensive stance",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Picking up low balls, low catching and fielding posture",
        "fit": "Direct"
      },
      "football": {
        "application": "Lowering centre of mass for defending, tackling and receiving",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "stretching-extending",
    "name": "Stretching / Extending",
    "category": "Stability / Non-locomotor",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – corrected",
    "reviewNotes": "Separated from 'reaching' and 'lunging' because stretching is primarily a mobility/non-locomotor pattern.",
    "bySport": {
      "badminton": {
        "application": "Mobility work and extended reach positions; not the same as a lunge",
        "fit": "Transfer"
      },
      "cricket": {
        "application": "Mobility work; extending to make the crease or reach a catch",
        "fit": "Transfer"
      },
      "football": {
        "application": "Mobility work and extending a leg/body to block or reach the ball",
        "fit": "Transfer"
      }
    }
  },
  {
    "id": "twisting",
    "name": "Twisting",
    "category": "Stability / Non-locomotor",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Direct foundation.",
    "bySport": {
      "badminton": {
        "application": "Torso rotation in overhead, backhand and recovery movements",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Hip/torso rotation in batting and bowling",
        "fit": "Direct"
      },
      "football": {
        "application": "Shielding, turning away from pressure and striking/crossing mechanics",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "turning",
    "name": "Turning",
    "category": "Stability / Non-locomotor",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Direct foundation.",
    "bySport": {
      "badminton": {
        "application": "Turning from stroke position back toward base/recovery",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Turning at the crease and reorienting while fielding",
        "fit": "Direct"
      },
      "football": {
        "application": "Turning with/without the ball and reorienting to play",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "swinging",
    "name": "Swinging",
    "category": "Stability / Non-locomotor",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Original – refined",
    "reviewNotes": "Direct foundation; object contact itself is classified under striking/kicking.",
    "bySport": {
      "badminton": {
        "application": "Racket swing and follow-through",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Bat swing; bowling arm swing is a related rotational action",
        "fit": "Direct"
      },
      "football": {
        "application": "Leg swing path for passing, crossing and shooting",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "swaying",
    "name": "Swaying",
    "category": "Stability / Non-locomotor",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Added",
    "reviewNotes": "Common non-locomotor movement omitted from the original list.",
    "bySport": {
      "badminton": {
        "application": "Weight shift and body adjustment during defensive/overhead preparation",
        "fit": "Transfer"
      },
      "cricket": {
        "application": "Body sway/weight shift when evading a short ball or adjusting balance",
        "fit": "Direct"
      },
      "football": {
        "application": "Body feints, shielding and balance adjustments",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "inverting-inversion",
    "name": "Inverting / Inversion",
    "category": "Stability / Non-locomotor",
    "frameworkLayer": "Advanced / Optional Extension",
    "reviewStatus": "Original – reclassified",
    "reviewNotes": "Keep only as an advanced extension; it should not sit at the same foundational priority as balance/landing.",
    "bySport": {
      "badminton": {
        "application": "Rare; not a foundational badminton requirement",
        "fit": "Limited"
      },
      "cricket": {
        "application": "Rare acrobatic fielding situations; not a foundational cricket requirement",
        "fit": "Limited"
      },
      "football": {
        "application": "Advanced goalkeeper acrobatics and bicycle/scissor-kick actions",
        "fit": "Limited"
      }
    }
  },
  {
    "id": "landing",
    "name": "Landing",
    "category": "Stability / Non-locomotor",
    "frameworkLayer": "Sport Movement Foundation",
    "reviewStatus": "Original – reclassified",
    "reviewNotes": "Critical sport foundation and injury-risk control skill.",
    "bySport": {
      "badminton": {
        "application": "Safe landing after jump smash, overhead interception or single-leg reach",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Safe landing after high catches, jumps and dives",
        "fit": "Direct"
      },
      "football": {
        "application": "Landing after headers, aerial duels and goalkeeper jumps",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "bracing",
    "name": "Bracing",
    "category": "Stability / Non-locomotor",
    "frameworkLayer": "Sport Movement Foundation",
    "reviewStatus": "Original – reclassified",
    "reviewNotes": "Important force-control pattern, but not normally listed as a classical FMS.",
    "bySport": {
      "badminton": {
        "application": "Trunk stiffness/control when producing or absorbing force in lunges and strokes",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Front-leg/trunk control during bowling and batting; absorbing fielding impact",
        "fit": "Direct"
      },
      "football": {
        "application": "Trunk control during tackles, shielding, landing and striking",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "lunging",
    "name": "Lunging",
    "category": "Functional / Sport Movement",
    "frameworkLayer": "Sport Movement Foundation",
    "reviewStatus": "Added",
    "reviewNotes": "Major omission for badminton and useful across all three sports.",
    "bySport": {
      "badminton": {
        "application": "Core forecourt/rear-court reach and recovery pattern",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Long reach to field a ball, crease extension and low catching positions",
        "fit": "Direct"
      },
      "football": {
        "application": "Reaching tackles, interceptions and extended first-touch/shot positions",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "squatting",
    "name": "Squatting",
    "category": "Functional / Sport Movement",
    "frameworkLayer": "Sport Movement Foundation",
    "reviewStatus": "Added",
    "reviewNotes": "Foundational lower-body movement pattern with strong training relevance.",
    "bySport": {
      "badminton": {
        "application": "Low ready/defensive position; force production and landing preparation",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Wicketkeeping/fielding ready posture and strength foundation",
        "fit": "Direct"
      },
      "football": {
        "application": "Defensive posture, jumping/landing preparation and strength foundation",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "reaching",
    "name": "Reaching",
    "category": "Functional / Sport Movement",
    "frameworkLayer": "Sport Movement Foundation",
    "reviewStatus": "Added",
    "reviewNotes": "Distinct from stretching; this is an active task-oriented movement.",
    "bySport": {
      "badminton": {
        "application": "Reaching to contact distant shuttles while maintaining control",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Reaching for catches, stops and run-out attempts",
        "fit": "Direct"
      },
      "football": {
        "application": "Reaching a foot/body to intercept, tackle or control the ball",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "pivoting",
    "name": "Pivoting",
    "category": "Functional / Sport Movement",
    "frameworkLayer": "Sport Movement Foundation",
    "reviewStatus": "Added",
    "reviewNotes": "Useful explicit pattern for change of orientation.",
    "bySport": {
      "badminton": {
        "application": "Foot pivot/reorientation during recovery and stroke preparation",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Turning/pivoting at the crease and during fielding pickup/throw transitions",
        "fit": "Direct"
      },
      "football": {
        "application": "Pivot turns, opening the body and changing playing direction",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "acceleration",
    "name": "Acceleration",
    "category": "Functional / Sport Movement",
    "frameworkLayer": "Sport Movement Foundation",
    "reviewStatus": "Added",
    "reviewNotes": "Important sports-performance movement omitted from the original list.",
    "bySport": {
      "badminton": {
        "application": "Explosive first steps from split-step toward the shuttle",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Initial sprint between wickets and first steps toward a fielded ball",
        "fit": "Direct"
      },
      "football": {
        "application": "First-step burst into runs, pressing, dribbling and defensive recovery",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "deceleration",
    "name": "Deceleration",
    "category": "Functional / Sport Movement",
    "frameworkLayer": "Sport Movement Foundation",
    "reviewStatus": "Original – reclassified",
    "reviewNotes": "Important sport movement skill, not a classical locomotor FMS.",
    "bySport": {
      "badminton": {
        "application": "Braking before the net, stroke setup and recovery",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Slowing before the crease/boundary and controlling approach to the ball",
        "fit": "Direct"
      },
      "football": {
        "application": "Braking before cuts, tackles, receiving and changes of direction",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "change-of-direction-cod",
    "name": "Change of Direction (COD)",
    "category": "Functional / Sport Movement",
    "frameworkLayer": "Sport Movement Foundation",
    "reviewStatus": "Added",
    "reviewNotes": "Essential across all three and should be explicit rather than hidden under turning/dodging.",
    "bySport": {
      "badminton": {
        "application": "Repeated direction changes between base and corners",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Turning for multiple runs; changing line while fielding/chasing",
        "fit": "Direct"
      },
      "football": {
        "application": "Cutting, pressing, recovery and dribbling direction changes",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "receiving-trapping",
    "name": "Receiving / Trapping",
    "category": "Object Control / Manipulative",
    "frameworkLayer": "Object-Control Extension",
    "reviewStatus": "Added",
    "reviewNotes": "Especially important for football; useful as an object-control extension.",
    "bySport": {
      "badminton": {
        "application": "Soft racket control/absorption is analogous, but not a true trap because the shuttle cannot be held",
        "fit": "Transfer"
      },
      "cricket": {
        "application": "Stopping/collecting a ground ball before a throw; catching covers airborne receipt",
        "fit": "Direct"
      },
      "football": {
        "application": "First touch, trapping and cushioning the ball with foot/thigh/chest",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "striking-head-body",
    "name": "Striking (Head / Body)",
    "category": "Object Control / Manipulative",
    "frameworkLayer": "Object-Control Extension",
    "reviewStatus": "Added",
    "reviewNotes": "Captures a major football object-control action not covered by kicking or hand/implement striking.",
    "bySport": {
      "badminton": {
        "application": "No direct match use",
        "fit": "N/A"
      },
      "cricket": {
        "application": "No core intentional striking skill; body deflections are incidental/rule-contextual",
        "fit": "N/A"
      },
      "football": {
        "application": "Heading and controlled chest/body redirection",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "tracking",
    "name": "Tracking",
    "category": "Perceptual-Motor",
    "frameworkLayer": "Perceptual-Motor Foundation",
    "reviewStatus": "Original – reclassified",
    "reviewNotes": "Very important, but perceptual-motor rather than a classical movement skill.",
    "bySport": {
      "badminton": {
        "application": "Reading shuttle flight, speed, spin and opponent contact cues",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Watching ball trajectory off bowler/pitch/bat and during catches",
        "fit": "Direct"
      },
      "football": {
        "application": "Reading passes, crosses, shots and ball flight",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "scanning",
    "name": "Scanning",
    "category": "Perceptual-Motor",
    "frameworkLayer": "Perceptual-Motor Foundation",
    "reviewStatus": "Original – reclassified",
    "reviewNotes": "Perceptual-cognitive foundation, not a classical FMS.",
    "bySport": {
      "badminton": {
        "application": "Monitoring opponent position, court space and likely reply before/after contact",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Checking field placements, runners, ball and tactical space",
        "fit": "Direct"
      },
      "football": {
        "application": "Looking over shoulder and checking teammates/opponents before receiving or moving",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "reaction-response",
    "name": "Reaction / Response",
    "category": "Perceptual-Motor",
    "frameworkLayer": "Perceptual-Motor Foundation",
    "reviewStatus": "Added",
    "reviewNotes": "Major perceptual-motor omission for all three sports.",
    "bySport": {
      "badminton": {
        "application": "Responding to opponent contact, deception and sudden changes in shuttle direction",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Reacting to release, edge/catch opportunities, deflections and fielding changes",
        "fit": "Direct"
      },
      "football": {
        "application": "Reacting to passes, rebounds, opponent movement and goalkeeper cues",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "spatial-awareness-orientation",
    "name": "Spatial Awareness / Orientation",
    "category": "Perceptual-Motor",
    "frameworkLayer": "Perceptual-Motor Foundation",
    "reviewStatus": "Added",
    "reviewNotes": "Core to safe and effective movement in dynamic sport environments.",
    "bySport": {
      "badminton": {
        "application": "Knowing body/court position relative to lines, base and opponent",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Awareness of crease, boundary, fielders, wickets and ball",
        "fit": "Direct"
      },
      "football": {
        "application": "Awareness of pitch zones, teammates, opponents, touchlines and goal",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "timing-rhythm",
    "name": "Timing / Rhythm",
    "category": "Perceptual-Motor",
    "frameworkLayer": "Perceptual-Motor Foundation",
    "reviewStatus": "Added",
    "reviewNotes": "Cross-sport foundation that links perception to movement execution.",
    "bySport": {
      "badminton": {
        "application": "Split-step timing, stroke timing and movement rhythm",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Batting timing, bowling rhythm, catching timing and running cadence",
        "fit": "Direct"
      },
      "football": {
        "application": "Pass/shot timing, run timing, tackle timing and movement rhythm",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "eye-hand-eye-foot-coordination",
    "name": "Eye-Hand / Eye-Foot Coordination",
    "category": "Perceptual-Motor",
    "frameworkLayer": "Perceptual-Motor Foundation",
    "reviewStatus": "Added",
    "reviewNotes": "Broad coordination foundation; can later be decomposed into sport-specific assessment items.",
    "bySport": {
      "badminton": {
        "application": "Eye-hand-racket coordination for shuttle contact",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Eye-hand-bat and eye-hand catching/throwing coordination",
        "fit": "Direct"
      },
      "football": {
        "application": "Eye-foot coordination for first touch, passing and shooting; eye-hand for goalkeepers",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "crawling",
    "name": "Crawling",
    "category": "Locomotor / Ground Movement",
    "frameworkLayer": "Broad Movement Foundation",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Important early gross-motor pattern and useful for gymnastics, combat sports, obstacle sports and general physical literacy.",
    "bySport": {
      "badminton": {
        "application": "Ground-based coordination, mobility and warm-up patterns; not a normal rally movement",
        "fit": "Transfer"
      },
      "cricket": {
        "application": "Ground-movement coordination in warm-up/conditioning and recovery drills",
        "fit": "Transfer"
      },
      "football": {
        "application": "Ground-movement coordination, recovery and conditioning drills",
        "fit": "Transfer"
      }
    }
  },
  {
    "id": "climbing",
    "name": "Climbing",
    "category": "Locomotor / Vertical Movement",
    "frameworkLayer": "Broad Movement Foundation",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Essential in climbing, adventure and obstacle sports; useful as a broad physical-literacy capability.",
    "bySport": {
      "badminton": {
        "application": "General strength/coordination training; no direct match action",
        "fit": "Transfer"
      },
      "cricket": {
        "application": "General strength/coordination training; no direct match action",
        "fit": "Transfer"
      },
      "football": {
        "application": "General strength/coordination training; no direct match action",
        "fit": "Transfer"
      }
    }
  },
  {
    "id": "pushing",
    "name": "Pushing",
    "category": "Functional Strength / Task Movement",
    "frameworkLayer": "Broad Movement Foundation",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "A universal force-production pattern; direct in many combat, strength and adaptive sports.",
    "bySport": {
      "badminton": {
        "application": "Pushing against the ground during lunges, starts and recovery; resistance training",
        "fit": "Transfer"
      },
      "cricket": {
        "application": "Ground-force production in sprinting/bowling and resistance training",
        "fit": "Transfer"
      },
      "football": {
        "application": "Ground-force production, shielding/contact preparation and resistance training",
        "fit": "Transfer"
      }
    }
  },
  {
    "id": "pulling",
    "name": "Pulling",
    "category": "Functional Strength / Task Movement",
    "frameworkLayer": "Broad Movement Foundation",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "A universal strength/task pattern; direct in rowing, climbing, grappling and strength sports.",
    "bySport": {
      "badminton": {
        "application": "Upper-body/scapular control and resistance training supporting racket actions",
        "fit": "Transfer"
      },
      "cricket": {
        "application": "Upper-body/back strength and resistance training supporting batting/throwing",
        "fit": "Transfer"
      },
      "football": {
        "application": "Upper-body strength for contact, posture and resistance training",
        "fit": "Transfer"
      }
    }
  },
  {
    "id": "carrying",
    "name": "Carrying",
    "category": "Functional Strength / Task Movement",
    "frameworkLayer": "Broad Movement Foundation",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Relevant to general physical capacity and directly central in some sports/tasks.",
    "bySport": {
      "badminton": {
        "application": "Carrying equipment and loaded-carry training; not a core rally action",
        "fit": "Transfer"
      },
      "cricket": {
        "application": "Running while carrying the bat; carrying equipment and loaded-carry training",
        "fit": "Direct"
      },
      "football": {
        "application": "Goalkeeper ball carry and loaded-carry training; outfield carrying by hand is restricted",
        "fit": "Limited"
      }
    }
  },
  {
    "id": "hanging-suspending",
    "name": "Hanging / Suspending",
    "category": "Stability / Body Support",
    "frameworkLayer": "Broad Movement Foundation",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Core in gymnastics, climbing and obstacle sports and useful for upper-body physical literacy.",
    "bySport": {
      "badminton": {
        "application": "Grip, shoulder and trunk control in conditioning; no direct rally action",
        "fit": "Transfer"
      },
      "cricket": {
        "application": "Grip/shoulder conditioning; no direct match action",
        "fit": "Transfer"
      },
      "football": {
        "application": "Grip/shoulder/trunk conditioning; no direct match action",
        "fit": "Transfer"
      }
    }
  },
  {
    "id": "lifting-lowering",
    "name": "Lifting / Lowering",
    "category": "Functional Strength / Task Movement",
    "frameworkLayer": "Broad Movement Foundation",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Universal task/strength pattern; central in weightlifting and strength sports.",
    "bySport": {
      "badminton": {
        "application": "Strength training and safe equipment handling",
        "fit": "Transfer"
      },
      "cricket": {
        "application": "Strength training and safe equipment handling",
        "fit": "Transfer"
      },
      "football": {
        "application": "Strength training and safe equipment handling",
        "fit": "Transfer"
      }
    }
  },
  {
    "id": "dragging",
    "name": "Dragging",
    "category": "Functional Strength / Task Movement",
    "frameworkLayer": "Broad Movement Foundation",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Useful universal movement pattern for strength, rescue, obstacle and conditioning contexts.",
    "bySport": {
      "badminton": {
        "application": "Resistance/conditioning drills; no direct rally action",
        "fit": "Transfer"
      },
      "cricket": {
        "application": "Resistance/conditioning drills; no direct match action",
        "fit": "Transfer"
      },
      "football": {
        "application": "Resistance/conditioning drills and contact-strength preparation",
        "fit": "Transfer"
      }
    }
  },
  {
    "id": "body-rolling-tumbling",
    "name": "Body Rolling / Tumbling",
    "category": "Locomotor / Ground Movement",
    "frameworkLayer": "Broad Movement Foundation",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Distinct from rolling an object. Important in gymnastics, martial arts, parkour and safe-falling education.",
    "bySport": {
      "badminton": {
        "application": "Safe recovery from a dive/fall; mainly training or advanced movement",
        "fit": "Limited"
      },
      "cricket": {
        "application": "Recovery after diving fielding actions and safe ground transitions",
        "fit": "Direct"
      },
      "football": {
        "application": "Safe fall/roll recovery, especially goalkeepers and contact situations",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "weight-transfer",
    "name": "Weight Transfer",
    "category": "Stability / Non-locomotor",
    "frameworkLayer": "Broad Movement Foundation",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "A core movement concept explicitly used in broad PE frameworks.",
    "bySport": {
      "badminton": {
        "application": "Transfer of body weight through split-step, lunge, stroke preparation and recovery",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Weight transfer in batting, bowling, throwing and fielding",
        "fit": "Direct"
      },
      "football": {
        "application": "Weight transfer in passing, shooting, turning, tackling and acceleration",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "vaulting",
    "name": "Vaulting",
    "category": "Locomotor / Body Support",
    "frameworkLayer": "Advanced / Optional Extension",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Important for gymnastics, parkour and obstacle sports but not a universal sport-specific requirement.",
    "bySport": {
      "badminton": {
        "application": "No normal match requirement",
        "fit": "N/A"
      },
      "cricket": {
        "application": "Rare boundary/obstacle situations; not a standard cricket skill",
        "fit": "Limited"
      },
      "football": {
        "application": "Rare obstacle/goalkeeper situations; not a standard football skill",
        "fit": "Limited"
      }
    }
  },
  {
    "id": "safe-falling-ground-recovery",
    "name": "Safe Falling / Ground Recovery",
    "category": "Stability / Ground Movement",
    "frameworkLayer": "Sport Movement Foundation",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Safety-critical foundation; progressions should be age-, surface- and sport-appropriate.",
    "bySport": {
      "badminton": {
        "application": "Managing accidental falls/dives and returning safely to play",
        "fit": "Limited"
      },
      "cricket": {
        "application": "Diving fielding/catching, safe fall mechanics and recovery to feet",
        "fit": "Direct"
      },
      "football": {
        "application": "Falls after tackles/aerial duels and goalkeeper ground recovery",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "grasping-gripping",
    "name": "Grasping / Gripping",
    "category": "Object Control / Hand Control",
    "frameworkLayer": "Broad Movement Foundation",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Fundamental hand-object interface; especially important in racket, bat, throwing, climbing and strength sports.",
    "bySport": {
      "badminton": {
        "application": "Racket grip, grip changes and controlled finger pressure",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Bat grip, ball grip for bowling/throwing and catching control",
        "fit": "Direct"
      },
      "football": {
        "application": "Goalkeeper ball handling and throw-in grip; limited for outfield hand use",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "releasing",
    "name": "Releasing",
    "category": "Object Control / Hand Control",
    "frameworkLayer": "Broad Movement Foundation",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Pairs naturally with grasping/gripping and is foundational to throwing/serving skills.",
    "bySport": {
      "badminton": {
        "application": "Controlled shuttle release for serves and feeding drills",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Ball release in bowling and throwing",
        "fit": "Direct"
      },
      "football": {
        "application": "Ball release in throw-ins and goalkeeper distribution",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "sidearm-throw",
    "name": "Sidearm Throw",
    "category": "Object Control / Manipulative",
    "frameworkLayer": "Object-Control Extension",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Completes common throwing directions alongside underhand and overhand.",
    "bySport": {
      "badminton": {
        "application": "No direct match use",
        "fit": "N/A"
      },
      "cricket": {
        "application": "Fast low-release fielding throws and relays",
        "fit": "Direct"
      },
      "football": {
        "application": "Goalkeeper sidearm distribution; occasional throw-in variations are still two-handed",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "bouncing-object",
    "name": "Bouncing (Object)",
    "category": "Object Control / Manipulative",
    "frameworkLayer": "Traditional FMS",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "SHAPE America includes bouncing a ball in early motor-skill learning; direct in basketball/handball-type sports.",
    "bySport": {
      "badminton": {
        "application": "No direct match application",
        "fit": "N/A"
      },
      "cricket": {
        "application": "No standard intentional dribble/bounce skill in match play",
        "fit": "N/A"
      },
      "football": {
        "application": "Goalkeepers may bounce the ball while in possession; otherwise no outfield hand dribble",
        "fit": "Limited"
      }
    }
  },
  {
    "id": "serving-initiating-object",
    "name": "Serving / Initiating Object",
    "category": "Object Control / Manipulative",
    "frameworkLayer": "Object-Control Extension",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Useful cross-sport object-control category for racket, volleyball and similar sports.",
    "bySport": {
      "badminton": {
        "application": "Serve with a short-handled implement: low, flick, drive and high serve variations",
        "fit": "Direct"
      },
      "cricket": {
        "application": "No 'serve' action; bowling is a distinct delivery skill",
        "fit": "N/A"
      },
      "football": {
        "application": "No serve; restarts are kicks/throw-ins and belong under those object-control skills",
        "fit": "N/A"
      }
    }
  },
  {
    "id": "swimming-aquatic-propulsion",
    "name": "Swimming / Aquatic Propulsion",
    "category": "Environment / Travel",
    "frameworkLayer": "Equipment & Environment Movement",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Required for aquatic sports and water safety; included because the master list is intended to span all sports/environments.",
    "bySport": {
      "badminton": {
        "application": "No direct match use; possible cross-training only",
        "fit": "N/A"
      },
      "cricket": {
        "application": "No direct match use; possible cross-training only",
        "fit": "N/A"
      },
      "football": {
        "application": "No direct match use; possible cross-training only",
        "fit": "N/A"
      }
    }
  },
  {
    "id": "floating-treading-water",
    "name": "Floating / Treading Water",
    "category": "Environment / Stability",
    "frameworkLayer": "Equipment & Environment Movement",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Aquatic stability/survival capability; relevant to swimming, water polo, lifesaving and aquatic recreation.",
    "bySport": {
      "badminton": {
        "application": "No direct match use",
        "fit": "N/A"
      },
      "cricket": {
        "application": "No direct match use",
        "fit": "N/A"
      },
      "football": {
        "application": "No direct match use",
        "fit": "N/A"
      }
    }
  },
  {
    "id": "gliding-skating",
    "name": "Gliding / Skating",
    "category": "Environment / Travel",
    "frameworkLayer": "Equipment & Environment Movement",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Core travel pattern on ice or wheeled/skating surfaces; relevant to skating and ice sports.",
    "bySport": {
      "badminton": {
        "application": "No direct match use",
        "fit": "N/A"
      },
      "cricket": {
        "application": "No direct match use",
        "fit": "N/A"
      },
      "football": {
        "application": "No direct match use",
        "fit": "N/A"
      }
    }
  },
  {
    "id": "cycling-pedalling",
    "name": "Cycling / Pedalling",
    "category": "Moving with Equipment",
    "frameworkLayer": "Equipment & Environment Movement",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Core equipment-based locomotion for cycling disciplines and useful cross-training elsewhere.",
    "bySport": {
      "badminton": {
        "application": "Aerobic cross-training only",
        "fit": "Limited"
      },
      "cricket": {
        "application": "Aerobic cross-training only",
        "fit": "Limited"
      },
      "football": {
        "application": "Aerobic cross-training/recovery only",
        "fit": "Limited"
      }
    }
  },
  {
    "id": "wheeling-wheelchair-propulsion",
    "name": "Wheeling / Wheelchair Propulsion",
    "category": "Moving with Equipment / Adaptive",
    "frameworkLayer": "Equipment & Environment Movement",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Important inclusive movement capability; should not be omitted from an all-sports framework.",
    "bySport": {
      "badminton": {
        "application": "Direct in wheelchair para-badminton classes; chair positioning and acceleration",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Relevant in adapted/wheelchair cricket formats; not standard mainstream cricket",
        "fit": "Limited"
      },
      "football": {
        "application": "Relevant in wheelchair/adapted football variants; powerchair football uses powered mobility rather than hand propulsion",
        "fit": "Limited"
      }
    }
  },
  {
    "id": "paddling-rowing",
    "name": "Paddling / Rowing",
    "category": "Moving with Equipment",
    "frameworkLayer": "Equipment & Environment Movement",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Core propulsive pattern in rowing, canoe/kayak and paddle sports.",
    "bySport": {
      "badminton": {
        "application": "No direct match use; conditioning transfer possible",
        "fit": "Limited"
      },
      "cricket": {
        "application": "No direct match use; conditioning transfer possible",
        "fit": "Limited"
      },
      "football": {
        "application": "No direct match use; conditioning transfer possible",
        "fit": "Limited"
      }
    }
  },
  {
    "id": "skiing-snow-surface-locomotion",
    "name": "Skiing / Snow-Surface Locomotion",
    "category": "Moving with Equipment",
    "frameworkLayer": "Equipment & Environment Movement",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Captures snow/ice environment movement within an all-sport master framework.",
    "bySport": {
      "badminton": {
        "application": "No direct match use",
        "fit": "N/A"
      },
      "cricket": {
        "application": "No direct match use",
        "fit": "N/A"
      },
      "football": {
        "application": "No direct match use",
        "fit": "N/A"
      }
    }
  },
  {
    "id": "riding-board-control",
    "name": "Riding / Board Control",
    "category": "Moving with Equipment",
    "frameworkLayer": "Equipment & Environment Movement",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Covers balance/propulsion interfaces in surfing, skateboarding, snowboarding and related board sports.",
    "bySport": {
      "badminton": {
        "application": "No direct match use",
        "fit": "N/A"
      },
      "cricket": {
        "application": "No direct match use",
        "fit": "N/A"
      },
      "football": {
        "application": "No direct match use",
        "fit": "N/A"
      }
    }
  },
  {
    "id": "whole-body-coordination",
    "name": "Whole-Body Coordination",
    "category": "Coordination",
    "frameworkLayer": "Physical Capacity",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Australian Physical Literacy Framework explicitly includes coordination in the Physical domain.",
    "bySport": {
      "badminton": {
        "application": "Coordinating legs, trunk, non-racket arm and racket arm through movement/strokes",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Coordinating run-up, trunk, limbs and object control in batting/bowling/fielding",
        "fit": "Direct"
      },
      "football": {
        "application": "Coordinating running, ball control, trunk and limb actions under pressure",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "bilateral-coordination",
    "name": "Bilateral Coordination",
    "category": "Coordination",
    "frameworkLayer": "Psychomotor Control Extension",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Important from childhood onward; complexity increases with level rather than appearing only at elite level.",
    "bySport": {
      "badminton": {
        "application": "Coordinating dominant/non-dominant sides during footwork, balance and stroke preparation",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Two-sided coordination in batting, catching, throwing transitions and bowling support actions",
        "fit": "Direct"
      },
      "football": {
        "application": "Coordinating both sides for running, turning, dribbling, passing and defending",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "body-awareness-proprioception",
    "name": "Body Awareness / Proprioception",
    "category": "Perceptual-Motor",
    "frameworkLayer": "Psychomotor Control Extension",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Foundational sensorimotor capability supporting safe and efficient movement.",
    "bySport": {
      "badminton": {
        "application": "Awareness of limb/body position through lunges, overhead strokes and recovery",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Body-position control in batting, bowling, catching and fielding",
        "fit": "Direct"
      },
      "football": {
        "application": "Body-position control in first touch, turning, contact and aerial actions",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "anticipation",
    "name": "Anticipation",
    "category": "Perceptual-Motor",
    "frameworkLayer": "Psychomotor Control Extension",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Present from beginner learning in simple form; elite performance adds speed, uncertainty and richer cues.",
    "bySport": {
      "badminton": {
        "application": "Anticipating opponent stroke and shuttle direction from cues",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Anticipating delivery, shot direction, running opportunities and fielding outcomes",
        "fit": "Direct"
      },
      "football": {
        "application": "Anticipating passes, runs, rebounds and opponent actions",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "force-touch-control",
    "name": "Force / Touch Control",
    "category": "Perceptual-Motor / Object Control",
    "frameworkLayer": "Psychomotor Control Extension",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Ability to scale force is a core perception-action skill across precision and power sports.",
    "bySport": {
      "badminton": {
        "application": "Grading racket force for net shots, drops, clears and smashes",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Grading bat swing, throw and bowling force for task demands",
        "fit": "Direct"
      },
      "football": {
        "application": "Grading touch/pass/shot force for distance, surface and pressure",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "depth-distance-trajectory-perception",
    "name": "Depth / Distance / Trajectory Perception",
    "category": "Perceptual-Motor",
    "frameworkLayer": "Psychomotor Control Extension",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Supports timing, tracking and interception; it can be developed progressively at all levels.",
    "bySport": {
      "badminton": {
        "application": "Judging shuttle depth, speed and trajectory",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Judging ball length, bounce, flight and catch/throw distance",
        "fit": "Direct"
      },
      "football": {
        "application": "Judging pass/cross/shot flight, speed and interception distance",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "fine-hand-finger-control",
    "name": "Fine Hand / Finger Control",
    "category": "Fine Motor / Precision",
    "frameworkLayer": "Psychomotor Control Extension",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "The master framework should acknowledge fine motor control for precision sports even though most FMS lists emphasize gross motor skills.",
    "bySport": {
      "badminton": {
        "application": "Finger power, grip changes and fine racket-face control",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Fine grip/release control for spin/seam bowling, batting grip and ball handling",
        "fit": "Direct"
      },
      "football": {
        "application": "Goalkeeper hand control and throw-in release; less central for outfield players",
        "fit": "Limited"
      }
    }
  },
  {
    "id": "flexibility-mobility",
    "name": "Flexibility / Mobility",
    "category": "Physical Capacity",
    "frameworkLayer": "Physical Capacity",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Separate the capacity (flexibility/mobility) from the action 'stretching'. ASC lists flexibility as a Physical-domain element.",
    "bySport": {
      "badminton": {
        "application": "Range of motion needed for lunges, reaches, overhead strokes and safe recovery",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Range of motion for bowling, batting, fielding, sprinting and catching",
        "fit": "Direct"
      },
      "football": {
        "application": "Range of motion for running, kicking, tackling, turning and goalkeeping",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "agility",
    "name": "Agility",
    "category": "Physical Capacity",
    "frameworkLayer": "Physical Capacity",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Broader than planned change of direction because agility includes response to an external stimulus.",
    "bySport": {
      "badminton": {
        "application": "Rapid controlled repositioning in response to shuttle/opponent cues",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Rapid controlled fielding movement, pickups and reactive changes",
        "fit": "Direct"
      },
      "football": {
        "application": "Reactive changes of body position/direction in attack and defence",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "strength",
    "name": "Strength",
    "category": "Physical Capacity",
    "frameworkLayer": "Physical Capacity",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Physical capacity, not a motor skill; included because the master framework spans broader physical literacy.",
    "bySport": {
      "badminton": {
        "application": "Force production/stability for acceleration, lunging, jumping and strokes",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Force production for sprinting, batting, bowling, throwing and fielding",
        "fit": "Direct"
      },
      "football": {
        "application": "Force production for sprinting, jumping, kicking, tackling and shielding",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "muscular-endurance",
    "name": "Muscular Endurance",
    "category": "Physical Capacity",
    "frameworkLayer": "Physical Capacity",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "ASC Physical-domain element; should be assessed separately from movement-technique quality.",
    "bySport": {
      "badminton": {
        "application": "Repeated force/control over rallies, sessions and tournament play",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Repeated fielding/batting/bowling demands depending on role",
        "fit": "Direct"
      },
      "football": {
        "application": "Repeated running, duels, accelerations and technical actions",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "cardiovascular-endurance",
    "name": "Cardiovascular Endurance",
    "category": "Physical Capacity",
    "frameworkLayer": "Physical Capacity",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "ASC Physical-domain element; a fitness capacity rather than a motor skill.",
    "bySport": {
      "badminton": {
        "application": "Supports sustained training, repeated rallies and recovery between efforts",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Supports prolonged fielding and role-dependent match/training demands",
        "fit": "Direct"
      },
      "football": {
        "application": "Supports sustained running and repeated high-intensity actions across a match",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "speed",
    "name": "Speed",
    "category": "Physical Capacity",
    "frameworkLayer": "Physical Capacity",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "ASC Physical-domain element; distinguish maximal speed from acceleration and reaction.",
    "bySport": {
      "badminton": {
        "application": "Court movement and limb/racket movement speed",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Running speed and limb/ball release speed depending on role",
        "fit": "Direct"
      },
      "football": {
        "application": "Sprint speed and movement/ball-action speed",
        "fit": "Direct"
      }
    }
  },
  {
    "id": "power-explosiveness",
    "name": "Power / Explosiveness",
    "category": "Physical Capacity",
    "frameworkLayer": "Physical Capacity",
    "reviewStatus": "Added – universal superset",
    "reviewNotes": "Useful addition beyond the ASC element list because many sports require rapid force production.",
    "bySport": {
      "badminton": {
        "application": "Explosive push-off, jump and racket acceleration",
        "fit": "Direct"
      },
      "cricket": {
        "application": "Explosive sprinting, throwing, batting and bowling actions",
        "fit": "Direct"
      },
      "football": {
        "application": "Explosive sprinting, jumping, kicking and tackling actions",
        "fit": "Direct"
      }
    }
  }
];
